/**
 * 🖼️ PHOTO GALLERY - Gallery component with lightbox for driver verification documents
 */

import React, { useState } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  Close,
  NavigateNext,
  NavigateBefore,
  Download,
  CheckCircle,
  Cancel,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';

import { DocumentViewer } from './DocumentViewer';

export interface GalleryImage {
  url?: string;
  title: string;
  documentType: 'nin' | 'selfie' | 'license_front' | 'license_back';
  status?: 'approved' | 'pending' | 'rejected';
}

interface PhotoGalleryProps {
  images: GalleryImage[];
  driverName?: string;
  onApprove?: () => void;
  onReject?: () => void;
  showActions?: boolean;
  isLoading?: boolean;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  images,
  driverName,
  onApprove,
  onReject,
  showActions = false,
  isLoading = false
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);

  const handleImageClick = (index: number) => {
    if (images[index]?.url) {
      setSelectedImageIndex(index);
      setZoom(1);
    }
  };

  const handleCloseLightbox = () => {
    setSelectedImageIndex(null);
    setZoom(1);
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null) {
      const nextIndex = (selectedImageIndex + 1) % images.length;
      // Find next image that has a URL
      for (let i = 0; i < images.length; i++) {
        const checkIndex = (nextIndex + i) % images.length;
        if (images[checkIndex]?.url) {
          setSelectedImageIndex(checkIndex);
          setZoom(1);
          break;
        }
      }
    }
  };

  const handlePrevImage = () => {
    if (selectedImageIndex !== null) {
      const prevIndex = (selectedImageIndex - 1 + images.length) % images.length;
      // Find previous image that has a URL
      for (let i = 0; i < images.length; i++) {
        const checkIndex = (prevIndex - i + images.length) % images.length;
        if (images[checkIndex]?.url) {
          setSelectedImageIndex(checkIndex);
          setZoom(1);
          break;
        }
      }
    }
  };

  const handleDownload = (imageUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCompletionStats = () => {
    const uploaded = images.filter(img => img.url).length;
    const approved = images.filter(img => img.status === 'approved').length;
    const rejected = images.filter(img => img.status === 'rejected').length;
    
    return {
      total: images.length,
      uploaded,
      approved,
      rejected,
      pending: uploaded - approved - rejected,
      completionPercentage: Math.round((uploaded / images.length) * 100)
    };
  };

  const stats = getCompletionStats();
  const selectedImage = selectedImageIndex !== null ? images[selectedImageIndex] : null;
  const hasImagesWithUrls = images.some(img => img.url);

  return (
    <Box>
      {/* Gallery Header */}
      {driverName && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Driver Documents - {driverName}
          </Typography>
          
          {/* Stats chips */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip 
              label={`${stats.uploaded}/${stats.total} Uploaded`}
              color={stats.uploaded === stats.total ? 'success' : 'warning'}
              size="small"
            />
            <Chip 
              label={`${stats.completionPercentage}% Complete`}
              color={stats.completionPercentage === 100 ? 'success' : 'primary'}
              size="small"
            />
            {stats.approved > 0 && (
              <Chip 
                label={`${stats.approved} Approved`}
                color="success"
                size="small"
                icon={<CheckCircle />}
              />
            )}
            {stats.rejected > 0 && (
              <Chip 
                label={`${stats.rejected} Rejected`}
                color="error"
                size="small"
                icon={<Cancel />}
              />
            )}
          </Stack>
        </Box>
      )}

      {/* Image Grid */}
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid item xs={12} sm={6} md={3} key={image.documentType}>
            <Box
              onClick={() => handleImageClick(index)}
              sx={{ 
                cursor: image.url ? 'pointer' : 'default',
                '&:hover': image.url ? {
                  transform: 'scale(1.02)',
                  transition: 'transform 0.2s ease-in-out'
                } : {}
              }}
            >
              <DocumentViewer
                imageUrl={image.url}
                title={image.title}
                documentType={image.documentType}
                isLoading={isLoading}
                showStatus={true}
                status={image.status}
                width={200}
                height={150}
                onViewFull={image.url ? () => handleImageClick(index) : undefined}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Action buttons */}
      {showActions && hasImagesWithUrls && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          {onApprove && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={onApprove}
              disabled={stats.uploaded < stats.total}
            >
              Approve All Documents
            </Button>
          )}
          {onReject && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={onReject}
            >
              Reject Application
            </Button>
          )}
        </Box>
      )}

      {/* Lightbox Modal */}
      <Dialog
        open={selectedImageIndex !== null}
        onClose={handleCloseLightbox}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'black', color: 'white' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0,0,0,0.8)',
          color: 'white'
        }}>
          <Typography variant="h6">
            {selectedImage?.title}
            {selectedImage?.status && (
              <Chip 
                label={selectedImage.status}
                size="small"
                color={selectedImage.status === 'approved' ? 'success' : 
                       selectedImage.status === 'rejected' ? 'error' : 'warning'}
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          <IconButton onClick={handleCloseLightbox} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 0, 
          bgcolor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400
        }}>
          {selectedImage?.url && (
            <Box sx={{ 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%'
            }}>
              {/* Navigation buttons */}
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <NavigateBefore />
              </IconButton>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <NavigateNext />
              </IconButton>

              {/* Zoom controls */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                gap: 1,
                zIndex: 1
              }}>
                <IconButton
                  onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                >
                  <ZoomOut />
                </IconButton>
                <IconButton
                  onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                >
                  <ZoomIn />
                </IconButton>
              </Box>

              {/* Main image */}
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.2s ease-in-out'
                }}
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)' }}>
          <Button
            onClick={() => selectedImage?.url && handleDownload(
              selectedImage.url, 
              `${selectedImage.title.replace(/\s+/g, '_')}.jpg`
            )}
            startIcon={<Download />}
            sx={{ color: 'white' }}
          >
            Download
          </Button>
          <Typography variant="caption" sx={{ color: 'grey.400', mx: 2 }}>
            {selectedImageIndex !== null && `${selectedImageIndex + 1} of ${images.filter(img => img.url).length}`}
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );
};